import { DrmLinkMapLayerFactory } from "./../layerFactory/DrmLinkMapLayerFactory";
import { getIntersection } from "ol/extent";
import { Feature } from "ol";
import { GeoJSON } from "ol/format";
import booleanIntersects from "@turf/boolean-intersects";
import { GeoMap } from "../GeoMap";
import { ExportValue } from "@/attribute/convert/ExportValue";
import { ZoomLevelError } from "../define/ZoomLevelError";
import { UnexportableLayerError } from "../define/UnexportableLayerError";
import { TraVosLayerFactory } from "../layerFactory/TraVosLayerFactory";
import { ODNodeLayerFactory } from "../layerFactory/ODNodeLayerFactory";
import { ODSumDeparturesLayerFactory } from "../layerFactory/ODSumDeparturesLayerFactory";
import { ODZoneLayerFactory } from "../layerFactory/ODZoneLayerFactory";
import { StdMapTileLayerFactory } from "../layerFactory/StdMapTileLayerFactory";
import { BlankMapTileLayerFactory } from "../layerFactory/BlankMapTileLayerFactory";
import { SeamlessphotoMapTileLayerFactory } from "../layerFactory/SeamlessphotoMapTileLayerFactory";
import { PaleMapTileLayerFactory } from "../layerFactory/PaleMapTileLayerFactory";
import { GeoJsonFactory } from "../layerFactory/GeoJsonFactory";

/**
 * DRMリンク最低ズームレベル
 */
const DRM_LINK_MIN_ZOOM_LEVEL = 17;

/**
 * CSV・GeoJson出力可レイヤファクトリ群
 */
const EXPORTABLE_LAYER_FACTORY = [
  TraVosLayerFactory,
  ODNodeLayerFactory,
  ODSumDeparturesLayerFactory,
  ODZoneLayerFactory,
];

/**
 * 背景地図レイヤファクトリ群
 */
const BACKGROUND_LAYER_FACTORY = [
  StdMapTileLayerFactory,
  BlankMapTileLayerFactory,
  SeamlessphotoMapTileLayerFactory,
  PaleMapTileLayerFactory,
  GeoJsonFactory,
];

/**
 * 出力クラス
 */
export class ExportArea {
  /**
   * 地図クラス
   */
  private _geoMap: GeoMap;

  /**
   * 出力クラスコンストラクタ
   */
  constructor(map: GeoMap) {
    this._geoMap = map;
  }

  /**
   * 出力
   */
  async export(
    areaFeature: Feature,
    _exportFormat: string
  ): Promise<Array<ExportValue[]>> {
    const map = this._geoMap._map;
    const featureExtent = areaFeature.getGeometry().getExtent();
    const mapExtent = map.getView().calculateExtent(map.getSize());
    const commonExtent = getIntersection(featureExtent, mapExtent);
    const layerFactories = this._geoMap.getLayerFactory().getLayerFactory();

    if (
      (commonExtent && commonExtent.length && !isFinite(commonExtent[0])) ||
      !isFinite(commonExtent[1]) ||
      !isFinite(commonExtent[2]) ||
      !isFinite(commonExtent[3])
    ) {
      return null;
    }

    // 重複しているレイヤがないか確認
    const visibleLayerFactories = layerFactories.filter((layerFactory) => {
      return (
        layerFactory.getVisibleLayerCount() > 0 &&
        !BACKGROUND_LAYER_FACTORY.some((checkLayerFactory) => {
          return layerFactory instanceof checkLayerFactory;
        })
      );
    });

    if (!visibleLayerFactories.length) {
      return null;
    }
    const promises = visibleLayerFactories.map(async (layerFactory) => {
      if (layerFactory.isRangeFeatureInfo()) {
        const rangeFeatureList = await layerFactory.getRangeFeatureInfo(
          map,
          commonExtent
        );
        if (rangeFeatureList && rangeFeatureList.length) {
          // 指定エリアと交差している地物だけを返す
          const format = new GeoJSON();
          const areaFeatureObject = format.writeFeatureObject(areaFeature);
          const fromatRangeFeatureMap: Map<string, ExportValue[]> = new Map<string, ExportValue[]>();
          const formatRangeFeatureList: Array<ExportValue[]> = [];
          rangeFeatureList.forEach(rangeFeature => {
            if (booleanIntersects(
              format.writeFeatureObject(rangeFeature.selectFeature),
              areaFeatureObject
            )) {
              if (!fromatRangeFeatureMap.has(rangeFeature.attributeConvert.getFeatureType())) {
                const rangeFeatureList: ExportValue[] = [];
                rangeFeatureList.push(rangeFeature.attributeConvert.getExportValue());
                fromatRangeFeatureMap.set(rangeFeature.attributeConvert.getFeatureType(), rangeFeatureList)
              } else {
                const rangeFeatureList = fromatRangeFeatureMap.get(rangeFeature.attributeConvert.getFeatureType());
                rangeFeatureList.push(rangeFeature.attributeConvert.getExportValue());
              }
            }
          });
          fromatRangeFeatureMap.forEach((value) => {
            formatRangeFeatureList.push(value);
          });
          return formatRangeFeatureList
        } else {
          return;
        }
      }
    });
    const results = await Promise.all(promises);
    const result = results.flat().filter((e) => e);
    if (
      !result.length &&
      !visibleLayerFactories.every((targetLayerFactory) => {
        return EXPORTABLE_LAYER_FACTORY.some((checkLayerFactory) => {
          return targetLayerFactory instanceof checkLayerFactory;
        });
      })
    ) {
      throw new UnexportableLayerError(visibleLayerFactories);
    } else if (//下に移動する
      visibleLayerFactories[0] instanceof DrmLinkMapLayerFactory &&
      map.getView().getZoom() < DRM_LINK_MIN_ZOOM_LEVEL
    ) {
      throw new ZoomLevelError(visibleLayerFactories);
    }

    return result;
  }
}
